import { useEffect, useMemo, useState } from "react";
import apiUtils, { isApiError } from "../apiUtils";
import logger from "../utils/logger";
import {
  mapScheduleToEnriched,
  type EnrichedAppointment,
  type ScheduleAppointment,
} from "../utils/schedule";

export type ScheduleStatus = "loading" | "loaded" | "error";

const SCHEDULE_ENDPOINT = "/api/home/schedule";

const TICK_MS = 30_000;

export interface ProviderSchedule {
  status: ScheduleStatus;
  errorMsg: string | null;
  appointments: ScheduleAppointment[];
  enriched: EnrichedAppointment[];
  now: Date;
}

/**
 * Loads the authenticated/previewed provider's schedule for today and returns
 * enriched appointments plus a live `now` clock. Refetches when `email` changes
 * (drives the dev "preview as provider" selection). Owned once by HomePage and
 * shared across the schedule columns so the backend is queried a single time.
 *
 * Targets the synthetic `GET /api/home/schedule`, which returns the provider's
 * schedule directly (name, patient id, 12-hour time, visit type per entry).
 *
 * @param email - The provider identity to scope the schedule to.
 * @returns The schedule status, raw + enriched appointments, and a live clock.
 */
export const useProviderSchedule = (email: string): ProviderSchedule => {
  const [appointments, setAppointments] = useState<ScheduleAppointment[]>([]);
  const [status, setStatus] = useState<ScheduleStatus>("loading");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [now, setNow] = useState<Date>(() => new Date());

  // Live tick so the NOW/next card, countdown, and upcoming/recent split stay current.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const loadSchedule = async () => {
      setStatus("loading");
      setErrorMsg(null);

      const resp = await apiUtils.fetchData<ScheduleAppointment[]>(
        SCHEDULE_ENDPOINT,
        "GET",
      );
      if (isCancelled) return;
      if (isApiError(resp)) {
        logger.devOnly.warn("useProviderSchedule: schedule lookup failed", {
          error: resp.error,
        });
        setErrorMsg("Could not load your provider schedule.");
        setStatus("error");
        return;
      }

      setAppointments(Array.isArray(resp) ? resp : []);
      setStatus("loaded");
    };

    loadSchedule();
    return () => {
      isCancelled = true;
    };
  }, [email]);

  const enriched = useMemo(
    () => mapScheduleToEnriched(appointments, now),
    [appointments, now],
  );

  return { status, errorMsg, appointments, enriched, now };
};

export default useProviderSchedule;
